/**
 * Email verification service using Reoon API
 */
import { supabase } from '../lib/supabase.js'
import { extractLinkedInUsername } from './linkedinScraper.js'
import { backgroundTaskService } from './backgroundTaskService.js'

/**
 * Submit emails for verification and create a task
 * @param {Array} emails - Array of email addresses to verify
 * @param {string} apiKey - The Reoon API key
 * @param {boolean} includeRawData - Whether to include raw data in the task
 * @param {string} taskName - Optional name for the verification task
 * @returns {Promise<Object>} - Task creation result
 */
export const createVerificationTask = async (emails, apiKey, includeRawData = false, taskName = 'Email Verification') => {
  try {
    const response = await fetch('https://emailverifier.reoon.com/api/v1/create-bulk-verification-task/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: taskName,
        emails: emails,
        key: apiKey,
        include_raw_data: includeRawData
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.reason || 'Failed to create verification task');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating verification task:', error);
    throw error;
  }
};

/**
 * Get results of a verification task
 * @param {string} taskId - The task ID from createVerificationTask
 * @param {string} apiKey - The Reoon API key
 * @returns {Promise<Object>} - Verification results
 */
export const getVerificationResults = async (taskId, apiKey) => {
  try {
    const response = await fetch(
      `https://emailverifier.reoon.com/api/v1/get-result-bulk-verification-task/?key=${apiKey}&task_id=${taskId}`
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.reason || 'Failed to get verification results');
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting verification results:', error);
    throw error;
  }
};

/**
 * Check if a verification task is complete
 * @param {string} taskId - The task ID
 * @param {string} apiKey - The Reoon API key
 * @returns {Promise<boolean>} - True if task is complete
 */
export const isVerificationComplete = async (taskId, apiKey) => {
  const result = await getVerificationResults(taskId, apiKey);
  return result.status === 'completed';
};

/**
 * Poll for verification results until complete
 * @param {string} taskId - The task ID
 * @param {string} apiKey - The Reoon API key
 * @param {number} intervalMs - Polling interval in milliseconds
 * @param {number} maxAttempts - Maximum number of polling attempts
 * @returns {Promise<Object>} - Final verification results
 */
export const pollForVerificationResults = async (taskId, apiKey, intervalMs = 2000, maxAttempts = 30) => {
  let attempts = 0;
  
  return new Promise((resolve, reject) => {
    const checkResults = async () => {
      try {
        const results = await getVerificationResults(taskId, apiKey);
        
        if (results.status === 'completed') {
          return resolve(results);
        }
        
        attempts++;
        if (attempts >= maxAttempts) {
          return reject(new Error('Verification timed out'));
        }
        
        setTimeout(checkResults, intervalMs);
      } catch (error) {
        reject(error);
      }
    };
    
    checkResults();
  });
};

/**
 * Verify emails in bulk and filter only safe-to-send emails
 * @param {Array} emails - Array of email addresses to verify
 * @param {string} apiKey - The Reoon API key
 * @param {string} taskName - Optional name for the verification task
 * @returns {Promise<Object>} - Object containing safe emails and stats
 */
export const verifyBulkAndFilterSafe = async (emails, apiKey, taskName = 'API: Safe Filter') => {
  try {
    // Step 1: Create bulk verification task
    const taskResult = await createVerificationTask(emails, apiKey, false, taskName);
    const taskId = taskResult.task_id;
    
    // Step 2: Poll for completion
    const resultData = await pollForVerificationResults(taskId, apiKey);
    
    // Step 3: Filter safe emails
    const results = resultData.results || {};
    const safeEmails = Object.entries(results)
      .filter(([, details]) => details && details.is_safe_to_send === true)
      .map(([email, details]) => ({ email, details }));
    
    return {
      task_id: resultData.task_id,
      safe_emails: safeEmails,
      all_results: results,
      stats: {
        count_total: resultData.count_total,
        count_checked: resultData.count_checked,
        progress_percentage: resultData.progress_percentage,
        safe_count: safeEmails.length
      }
    };
  } catch (error) {
    console.error('Error in bulk verification:', error);
    throw error;
  }
};

/**
 * Save verified leads to Supabase
 * @param {Array} verifiedLeads - Array of verified lead objects
 * @param {string} sessionId - Session ID for grouping leads
 * @returns {Promise<Object>} - Supabase insert result
 */
export async function saveVerifiedLeads(leads, sessionId) {
  try {
    const leadsToSave = leads.map(lead => {
      const linkedinUrl = lead.linkedinUrl || lead.linkedin_url || lead.person_linkedin || '';
      const linkedinUsername = lead.linkedinUsername || lead.linkedin_username || 
        (linkedinUrl ? extractLinkedInUsername(linkedinUrl) : '') || '';
      
      return {
        email: lead.email,
        company: lead.company || '',
        first_name: lead.first_name || '',
        last_name: lead.last_name || '',
        job_title: lead.job_title || lead.title || '',
        url: lead.url || lead.company_website || lead.website || '',
        company_linkedin: lead.company_linkedin || '',
        person_linkedin: lead.person_linkedin || '',
        linkedin_url: linkedinUrl,
        linkedin_username: linkedinUsername,
        country: lead.country || '',
        industry: lead.industry || '',
        phone_number: (lead.phone_number || '').substring(0, 50), // Truncate to 50 chars to match DB constraint
        verification_status: 'verified'.substring(0, 50), // Truncate to 50 chars to match DB constraint
        is_safe_to_send: lead.is_safe_to_send || true,
        verification_details: lead.verification_details || {},
        raw_data: lead,
        session_id: sessionId
      };
    });

    const { data, error } = await supabase
      .from('verified_leads')
      .upsert(leadsToSave, {
        onConflict: 'email',
        ignoreDuplicates: false
      })
      .select();

    if (error) {
      throw error;
    }

    // Update session counts in analysis_sessions table
    try {
      const totalLeads = data.length;
      const safeLeads = data.filter(lead => lead.is_safe_to_send).length;
      
      await supabase
        .from('analysis_sessions')
        .update({
          total_leads: totalLeads,
          verified_leads: totalLeads,
          safe_leads: safeLeads,
          status: 'completed'
        })
        .eq('session_id', sessionId);
    } catch (updateError) {
      console.warn('Failed to update session counts:', updateError);
    }

    return { success: true, data, count: data.length };
  } catch (error) {
    console.error('Error saving verified leads:', error);
    throw error;
  }
};

/**
 * Get verified leads from Supabase
 * @param {string} sessionId - Session ID to filter leads
 * @returns {Promise<Array>} - Array of verified leads
 */
export const getVerifiedLeads = async (sessionId) => {
  try {
    const { data, error } = await supabase
      .from('verified_leads')
      .select('*')
      .eq('session_id', sessionId)
      .eq('verification_status', 'verified')
      .order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error getting verified leads:', error);
    throw error;
  }
};

/**
 * Update lead status (e.g., when selected for website analysis)
 * @param {Array} leadIds - Array of lead IDs to update
 * @param {string} status - New status
 * @returns {Promise<Object>} - Update result
 */
export const updateLeadStatus = async (leadIds, status) => {
  try {
    const { data, error } = await supabase
      .from('verified_leads')
      .update({ 
        status: (status || '').substring(0, 50), // Truncate to 50 chars to match DB constraint
        updated_at: new Date().toISOString()
      })
      .in('id', leadIds)
      .select();

    if (error) {
      throw error;
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error updating lead status:', error);
    throw error;
  }
};

/**
 * Start server-side background email verification
 * @param {Array} emails - Array of email addresses to verify
 * @param {Object} config - Verification configuration
 * @returns {Promise<Object>} - Background task object
 */
export const startBackgroundEmailVerification = async (emails, config = {}) => {
  try {
    const task = await backgroundTaskService.startEmailVerification(emails, {
      ...config,
      reoonApiKey: config.reoonApiKey || import.meta.env.VITE_REOON_API_KEY
    });
    
    console.log(`Started server-side background email verification: ${task.id}`);
    return task;
  } catch (error) {
    console.error('Failed to start background email verification:', error);
    throw error;
  }
};

/**
 * Get status of background email verification task
 * @param {string} taskId - Task ID
 * @returns {Promise<Object>} - Task status
 */
export const getBackgroundVerificationStatus = async (taskId) => {
  try {
    return await backgroundTaskService.getTask(taskId);
  } catch (error) {
    console.error('Failed to get background verification status:', error);
    return null;
  }
};

/**
 * Get results of background email verification
 * @param {string} taskId - Task ID
 * @returns {Promise<Object>} - Verification results
 */
export const getBackgroundVerificationResults = async (taskId) => {
  try {
    return await backgroundTaskService.getTaskResults(taskId);
  } catch (error) {
    console.error('Failed to get background verification results:', error);
    return null;
  }
};

/**
 * Cancel background email verification (if pending)
 * @param {string} taskId - Task ID
 */
export const cancelBackgroundVerification = async (taskId) => {
  try {
    await backgroundTaskService.cancelTask(taskId);
    console.log(`Cancelled background email verification: ${taskId}`);
  } catch (error) {
    console.error('Failed to cancel background verification:', error);
  }
};

/**
 * Get all user's background email verification tasks
 * @returns {Promise<Array>} - Array of verification tasks
 */
export const getActiveBackgroundVerificationTasks = async () => {
  try {
    return await backgroundTaskService.getUserTasks(null, 'email_verification');
  } catch (error) {
    console.error('Failed to get active background verification tasks:', error);
    return [];
  }
};

/**
 * Poll email verification task until completion with progress updates
 * @param {string} taskId - Task ID
 * @param {function} onProgress - Progress callback
 * @returns {Promise<Object>} - Final verification results
 */
export const pollBackgroundVerificationUntilComplete = async (taskId, onProgress = null) => {
  try {
    return await backgroundTaskService.pollTaskUntilComplete(taskId, onProgress);
  } catch (error) {
    console.error('Background email verification failed:', error);
    throw error;
  }
};

/**
 * Subscribe to real-time email verification task updates
 * @param {string} taskId - Task ID
 * @param {function} callback - Update callback
 * @returns {function} - Unsubscribe function
 */
export const subscribeToBackgroundVerificationUpdates = (taskId, callback) => {
  return backgroundTaskService.subscribeToTaskUpdates(taskId, callback);
};